import React, { useState, useEffect, useCallback } from "react";
import { Save, Printer, Loader, View } from "lucide-react";
import { Box, IconButton, Tab, ThemeProvider, Tooltip } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { HelpOutline } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import TimelineTab from "../salesInvoice/TimelineTab";
import ModeIndicator from "../salesInvoice/ModeIndicator";
import {
  DOCUMENT_TYPES,
  getDocumentConfig,
  fetchDocumentNumber,
  MODIFICATION_TYPES,
  tooltipContent,
  theme,
} from "../config/DocumentConfig";
import { purchaseData } from "../salesInvoice/data";
import { getTodayDate } from "../../utils/constants";
import { calculateRowValues } from "../config/PurchaseInvoiceTableConfig";
import Modal from "../common/CommonModal";
import ModificationCommentModal from "../salesInvoice/ModificationCommentModal";
import ProductionGeneral from "./ProductionGeneral";
import ProductionTable from "./ProductionTable";
import AssembleDismantleGeneral from "./AssembleDismantleGeneral";

const AssembleDisamble = ({ documentType = DOCUMENT_TYPES.PRODUCTION }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { request } = useApi();
  const config = getDocumentConfig(documentType);

  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  const CURRENT_USER = localStorage?.getItem("currentUserName") || "admin";
  let API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const todayDate = getTodayDate();

  const documentNoFromState = location?.state?.docNo || null;
  const modeFromState = location?.state?.mode || "new";
  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState(modeFromState);

  const [parentTabValue, setParentTabValue] = useState("1");

  const [autoVatIsOn, setAutoVatIsOn] = useState(true);
  const [vatAmountManual, setVatAmountManual] = useState(null);
  const [discountAmount, setDiscountAmount] = useState("");
  const [roundOff, setRoundOff] = useState("");

  const [items, setItems] = useState(purchaseData);
  const [initialRowUnitOptions, setInitialRowUnitOptions] = useState({});
  const [isStockManagementOpen, setIsStockManagementOpen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [currentLocation, setCurrentLocation] = useState("MAIN SHOP");
  const [itemInfo, setItemInfo] = useState({
    qtyInhand: 0,
    costAvg: 0,
    lSalesprice: 0,
    lpCost: 0,
    lCSalesprice: 0,
    pSoQty: 36,
    pPoQty: "RRD",
  });

  const [otherCosts, setOtherCosts] = useState([]);
  const [isOtherCostEnabled, setIsOtherCostEnabled] = useState(false);
  const [supplierData, setSupplierData] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isModificationModalOpen, setIsModificationModalOpen] = useState(false);
  const [bomLoading, setBomLoading] = useState(false);
  const [unitCostManual, setUnitCostManual] = useState(false);
  const [savedInvoiceNumber, setSavedInvoiceNumber] = useState(null);
  const [isOtherCostModalOpen, setIsOtherCostModalOpen] = useState(false);

  // Document data
  const [documentData, setDocumentData] = useState({
    documentNo: "",
    date: todayDate,
    paymentMode: "cash",
    invNo: "",
    supplier: "",
    unregSuppl: "",
    trn: "",
    dueDays: "",
    dueDate: todayDate,
    cfAgent: "",
    shipRef: "",
    docDate: todayDate,
    jobCode: "",
    remarks: "",
    currency: "AED",
    currencyRate: "1",
    voucherType: "",
    creditAcc: "",
    debitAcc: "",
    invNumber: "",
    vatAcc: "",
    crAccount: "",
    drAccount: "",
    woNo: "",
    otherCostsTotal: "0",
    productionQty: "0",
    productCode: "",
    productDescription: "",
    unitCost: "0",
  });
  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "";
  const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;

  const fetchTransferDocument = async (docNo) => {
    try {
      const masterUrl = `${BASE_URL}/api/CRMDocTransfer/${COMPANY_CODE}/TRANSFER/PRODUCTIONMASTER/-/-/${DEPARTMENT}/${docNo}/-`;
      const detailUrl = `${BASE_URL}/api/CRMDocTransfer/${COMPANY_CODE}/TRANSFER/PRODUCTIONDETAIL/-/-/${DEPARTMENT}/${docNo}/-`;

      const [masterRes, detailRes] = await Promise.all([
        fetch(masterUrl),
        fetch(detailUrl),
      ]);

      const masterData = await masterRes.json();
      const detailData = await detailRes.json();

      if (masterData && masterData.length > 0) {
        const main = masterData[0];
        setDocumentData((prev) => ({
          ...prev,
          documentNo: String(main.inv_no || ""),
          date: main.date ? main.date.slice(0, 10) : prev.date,
          currency: main.fc || prev.currency,
          currencyRate: main.rate || prev.currencyRate,
          remarks: main.remarks || prev.remarks,
          jobCode: main.jobCode || prev.jobCode,
          crAccount: (main.account || main.acc || "")?.toString().trim(),
          drAccount: (main.purchAccount || main.purchAcc || "")
            ?.toString()
            .trim(),
          productCode: main.accdesc,
          productDescription: main.inv,
          productionQty: Number(main.remarks) || main.Pending_Qty || 0,
          shipRef: main.shipref || prev.shipRef,
          woNo: main.CCNO || prev.woNo,
          otherCostsTotal: main.othercost || "0",
        }));
        setDiscountAmount(main.discount ?? main.discount ?? 0);
        setRoundOff(main.rdoff ?? 0);
      }

      if (detailData && detailData.length > 0) {
        const mappedItems = detailData.map((r, idx) => ({
          id: idx + 1,
          sino: idx + 1,
          code: r.code || "",
          description: r.idesc || r.Description || "",
          location: r.locn || "A",
          unit: r.unit || "PCS",
          qty: Number(r.tr_qty2 ?? r.tr_qty ?? r.tr_qty2) || 0,
          unitCost: Number(r.unit_cost ?? r.unit_price ?? r.funit_cost ?? 0),
          amount: Number(r.line_total ?? r.fline_tota ?? 0),
          unitBCCost: Number(r.funit_cost ?? r.unit_cost ?? 0),
          fraction: r.fraction ?? 1,
          total: Number(r.line_total ?? r.fline_tota ?? 0),
          cntrl: r.soGuid,
          isActive: r.Slno === "N" ? "No" : r.Slno === "Y" ? "Yes" : "Yes",
        }));
        setItems(mappedItems);
      }
    } catch (err) {
      console.error("Failed to fetch transfer document:", err);
      alert("Failed to load transferred production: " + err.message);
    }
  };

  const initializeDocument = useCallback(async () => {
    try {
      const { voucherType, documentNo } = await fetchDocumentNumber(
        documentType,
        documentData.paymentMode,
        BASE_URL,
        COMPANY_CODE,
        DEPARTMENT,
      );

      if (documentNo) {
        setDocumentData((prev) => ({
          ...prev,
          documentNo: documentNo,
          voucherType,
        }));
      }
      console.log("Voucher Type:", voucherType, documentNo);
    } catch (err) {
      console.error("Error initializing document:", err);
    }
  }, [
    documentType,
    documentData?.paymentMode,
    BASE_URL,
    COMPANY_CODE,
    DEPARTMENT,
  ]);

  useEffect(() => {
    const docNo = location?.state?.docNo;

    if (docNo && request) {
      fetchTransferDocument(docNo);
    }
  }, [location?.state?.docNo]);

  useEffect(() => {
    const workOrder = location?.state?.workOrder;
    console.log("Work Order from location.state:", workOrder);
    if (!workOrder) return;

    setDocumentData((prev) => ({
      ...prev,
      productCode:
        workOrder.productCode || workOrder.Product_Code || workOrder.code || "",
      productDescription:
        workOrder.description ||
        workOrder.Description ||
        workOrder.productDescription ||
        "",
      productionQty: workOrder.Pending_Qty || 0,
      woNo: workOrder.woNo || workOrder.Wo_No || prev.woNo || "",
      shipRef: workOrder.soUid || "",
    }));
  }, [location?.state?.workOrder]);

  // Handle product selection from stock management
  const handleProductSelect = useCallback(
    (product) => {
      if (product) {
        console.log("Selected Product:", product);
        setDocumentData((prev) => ({
          ...prev,
          productCode: product.Code || product.productCode || "",
          productDescription:
            product.Description || product.productDescription || "",
        }));
        setIsStockManagementOpen(false);
      }
    },
    [setDocumentData],
  );

  // Fetch BOM items when productCode is present

  const fetchBomItems = useCallback(async () => {
    const productCode = documentData?.productCode?.toString()?.trim();
    if (!productCode) {
      console.log("No product code provided");
      return;
    }

    try {
      setBomLoading(true);
      const url = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/BOMITEMS/${productCode}/-`;

      const resp = await request({ url, method: "GET" });

      const qtyMultiplier =
        Number(documentData.productionQty) ||
        Number(location?.state?.workOrder?.Pending_Qty) ||
        1;

      if (resp && resp.length > 0) {
        const mapped = resp.map((r, idx) => {
          const perUnitQty = Number(r.QTY || 0) || 0;
          const qty = perUnitQty * qtyMultiplier;
          const unitCost = Number(r.Cost || 0) || 0;
          const amount = Number((qty * unitCost).toFixed(2));
          return {
            id: idx + 1,
            sino: idx + 1,
            code: (r.CODE || "").toString().trim(),
            description: r.DESCRIPTION || "",
            location:
              r.locn || localStorage.getItem("departmentLocation") || "A",
            unit: r.unit || "PCS",
            qty: qty,
            unitCost: r.Cost || 0,
            unitBCCost: r.Cost,
            amount,
            othCost: 0,
            x: r.x || "",
            cntrl: r.cntrl || "",
            fraction: 1,
            vatEnabled: true,
            vatPercent: VATPERCENTAGE || 0,
            vatAmount: 0,
            total: amount,
            mastInfo: "",
            pono: 0,
            oem: "",
            group: "",
            "Discount%": 0,
            git: "",
            salesperson:
              location?.state?.workOrder?.salesPerson || CURRENT_USER,
            bin: "",
            remarks: r.Remarks || r.remark || "",
            dcp: "",
            isActive: "Yes",
          };
        });

        setItems(mapped);
        console.log("BOM items fetched successfully:", mapped);
      } else {
        console.log("No BOM items found for product code:", productCode);
      }
    } catch (err) {
      console.error("Failed to fetch BOM items:", err);
    } finally {
      setBomLoading(false);
    }
  }, [
    documentData?.productCode,
    documentData?.productionQty,
    BASE_URL,
    COMPANY_CODE,
    request,
    VATPERCENTAGE,
    CURRENT_USER,
    location?.state?.workOrder,
  ]);

  // Auto-fetch BOM items when productCode changes
  useEffect(() => {
    if (documentData?.productCode) {
      fetchBomItems();
    }
  }, [documentData?.productCode]);
  useEffect(() => {
    if (mode === "new") {
      initializeDocument();
    }
  }, [mode, initializeDocument]);

  // VAT calculation
  const getVatPercent = (documentData) =>
    documentData.vatStatus === "VAT Applied" ? VATPERCENTAGE : 0;

  const calculateTotals = () => {
    const round = (val) => Math.round(Number(val) * 100) / 100;

    // Filter out inactive items BEFORE processing
    const activeItems = items.filter(
      (item) => String(item.isActive || "").toLowerCase() !== "no",
    );

    const processedItems = activeItems.map((item) =>
      calculateRowValues(item, documentData, {
        items: activeItems, // Use filtered items
        otherCosts,
      }),
    );

    const rowTaxableSum = round(
      processedItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    );

    const rowVatSum = round(
      processedItems.reduce(
        (sum, item) => sum + (Number(item.vatAmount) || 0),
        0,
      ),
    );

    // Sum of other-cost rows (they have .total)
    const otherCostTotal = round(
      (otherCosts || []).reduce((sum, c) => sum + (Number(c.total) || 0), 0),
    );

    const grossAmount = round(
      processedItems.reduce((sum, item) => sum + (Number(item.total) || 0), 0),
    );

    const globalDiscount = Number(discountAmount) || 0;
    const roundOffValue = Number(roundOff) || 0;

    const taxableAmount = round(rowTaxableSum - globalDiscount - roundOffValue);

    const totalVat =
      !autoVatIsOn && vatAmountManual !== null
        ? round(vatAmountManual)
        : rowVatSum;

    const baseTotalAmount = round(taxableAmount + totalVat);

    // Add other-costs into final amount so unit cost accounts for them
    const totalAmountWithOther = round(baseTotalAmount + otherCostTotal);

    const netAmount = round(totalAmountWithOther);

    const currencyRate = Number(documentData.currencyRate) || 1;
    const bcAmt = round(netAmount * currencyRate);

    return {
      grossAmount: grossAmount.toFixed(2),
      taxableAmount: taxableAmount.toFixed(2),
      vatAmount: totalVat.toFixed(2),
      discountAmount: globalDiscount.toFixed(2),
      roundOff: roundOffValue.toFixed(2),
      netAmount: netAmount.toFixed(2),
      otherCharges: otherCostTotal.toFixed(2),
      bcAmt: bcAmt.toFixed(2),
    };
  };

  const totals = calculateTotals();

  // Auto-calc unit cost from (net amount + other costs) / productionQty
  useEffect(() => {
    const qty = Number(documentData?.productionQty) || 0;
    const netAmount = Number(totals?.netAmount) || 0;
    const otherCostsTotal = Number(documentData?.otherCostsTotal) || 0;

    let unitCost = 0;

    if (qty > 0) {
      unitCost = Math.round(((netAmount + otherCostsTotal) / qty) * 100) / 100;
    }

    setDocumentData((prev) => {
      const prevUnit = Number(prev.unitCost) || 0;
      if (prevUnit !== unitCost) {
        return { ...prev, unitCost };
      }
      return prev;
    });
  }, [
    totals?.netAmount,
    documentData?.productionQty,
    documentData?.otherCostsTotal,
  ]);

  useEffect(() => {
    const otherTotal = (otherCosts || []).reduce(
      (s, c) => s + (Number(c.total) || 0),
      0,
    );
    setDocumentData((prev) => ({
      ...prev,
      otherCostsTotal: otherTotal.toFixed(2),
    }));
  }, [otherCosts]);

  const refreshStockDocument = async (
    companyCode,
    documentNumber,
    department,
    fieldMap,
  ) => {
    const response = await fetch(
      `${BASE_URL}/api/CRMStockRefreshDoc/${companyCode}/${fieldMap.refreshType}/${documentNumber}/${department}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Stock refresh failed");
    }

    return response.json();
  };
  const handleSave = async () => {
    const productCode = documentData.productCode?.toString()?.trim();
    const productDesc = documentData.productDescription?.toString()?.trim();
    const productionQty = Number(documentData.productionQty || 0);

    if (!productCode) {
      alert("Product code is required to save production.");
      return;
    }
    if (!productionQty || productionQty <= 0) {
      alert("Production quantity must be greater than zero.");
      return;
    }
    if (
      items.filter((item) => item.code && item.code.toString().trim() !== "")
        .length === 0
    ) {
      alert("Please add at least one item before saving.");
      return;
    }

    setIsSaving(true);

    try {
      const calculatedItems = items.map((item) =>
        calculateRowValues(item, documentData, { items, otherCosts }),
      );

      const headerProductCode = productCode;
      const headerProductDesc =
        productDesc || calculatedItems[0]?.description || "";
      const headerRemarks = String(productionQty);

      const payload = calculatedItems
        .filter((item) => item.code && item.code.toString().trim() !== "")
        .map((item, idx) => {
          const qty = item.qty?.toString() || "0";
          const unitCost = (Number(item.unitCost) || 0).toString();
          const unitBCCost = (item.unitBCCost ?? item.unitCost ?? 0).toString();
          const amountLine = (Number(item.amount) || 0).toFixed(2);
          const vatAmtLine = (Number(item.vatAmount) || 0).toFixed(2);
          const totalLine = (Number(item.total) || 0).toFixed(2);

          return {
            cmpcode: COMPANY_CODE,
            modeop: documentNoFromState ? "EDIT" : "SAVE",
            inv_no: documentData.documentNo || "0000",
            ref: documentData.invNo || "",
            fc: documentData.currency || "AED",
            amount: totals.netAmount || "0",
            fcamount: totals.netAmount || "0",
            account: documentData.crAccount || "",
            accdesc: headerProductCode,
            status: "0",
            remarks: headerRemarks,
            deptno: DEPARTMENT,
            date: documentData.date || todayDate,
            inv: headerProductDesc || documentData.invNumber || "",
            shipDate: documentData.docDate || todayDate,
            shipref: documentData.shipRef || "",
            duedate: documentData.dueDate || todayDate,
            mawb: "",
            hwab: "",
            lcno: "",
            docref: "",
            cfagent: documentData.cfAgent || "",
            purchAccount: documentData.drAccount || "",
            purchAccDesc: CURRENT_USER,
            rate: documentData.currencyRate || "1",
            local: documentData.purchaseType === "Local" ? "0" : "1",
            othercost: documentData.otherCostsTotal || "0",
            jobCode: documentData.jobCode || "",
            discount: Number(discountAmount) || "0",
            fdiscount: "0",
            CCNO: documentData?.woNo || "",
            SERIALNO: "0",
            vatamt: totals.vatAmount || "0",
            rdoff: roundOff?.toString() || "0",
            costUpd: "NEW",
            docinfo: "",
            mrv_no: "0",
            p1: "0",
            p2: "0",
            vatacc: documentData.vatAcc || "",
            vataccImp: documentData.vatAccImp || "",
            method: documentData.paymentMode || "cash",
            cashcred: documentData.paymentMode === "cash" ? "CASH_PAID" : "",
            cashAcc: documentData.creditAcc || supplierData?.ACCOUNT || "",
            vatType: documentData.vatStatus || "VAT Applied",
            Slno: item.isActive === "Yes" ? "Y" : "N",
            code: item.code || "",
            description: item.description || "",
            locn: item.location || "",
            unit: item.unit || "",
            qty: qty,
            "Unit Cost": unitCost,
            "Unit BCCost": unitBCCost,
            AmountLine: amountLine,
            "Oth.Cost": (Number(item.othCost) || 0).toFixed(2),
            x: item.x || "",
            cntrl: item.cntrl || "",
            frac: item.fraction ?? 1,
            "vat%": (item.vatPercent || 0).toString(),
            VatamtLine: vatAmtLine,
            Mastinfo: item.mastInfo || "",
            pono: item.pono || "0",
            total: totalLine,
            oem: item.oem || "",
            group: item.group || "",
            "disc%": (Number(item["Discount%"]) || 0).toString(),
            git: item.git || "0",
            Salesperson: item.salesperson || "SUN001",
            bin: item.bin || "",
            RemarksLine: item.remarks || "",
            DCP: item.dcp || "0",
          };
        });

      console.log("Payload to save:", payload);

      const response = await fetch(`${BASE_URL}${config.apiEndpoints.save}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log("Save response:", result);

      if (response?.status === 200) {
        const invoiceNumber = result?.invoiceNo;
        await refreshStockDocument(COMPANY_CODE, invoiceNumber, DEPARTMENT);
        // Store the saved invoice number
        setSavedInvoiceNumber(invoiceNumber);

        // Update document data with the saved invoice number
        setDocumentData((prev) => ({
          ...prev,
          documentNo: invoiceNumber,
        }));

        alert(`${config.title} no: ${invoiceNumber} saved successfully.`);

        // Production.js (inside if response?.status === 200)
        if (otherCosts && otherCosts.length > 0) {
          const hasValidOtherCosts = otherCosts.some(
            (cost) => cost.empCode && cost.empCode.trim() !== "",
          );

          if (hasValidOtherCosts) {
            const wagePayload = otherCosts
              .filter((r) => r.empCode && r.empCode.trim() !== "")
              .map((r) => ({
                CmpCode: COMPANY_CODE,
                mode: "ENTRY",
                inv_no: Number(invoiceNumber), // IMPORTANT: use invoice number from response
                deptno: DEPARTMENT,
                empno: r.empCode,
                empname: r.empName,
                pdate: r.pdate || documentData.date || todayDate,
                jobno: r.jobno || documentData.jobCode || "",
                nr_hrs: Number(r.nrHrs || 0),
                nr_rate: Number(r.nrRate || 0),
                tot_wage: Number(r.total || 0),
              }));

            try {
              const wageRes = await fetch(`${BASE_URL}/api/CRMProdWageReg`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(wagePayload),
              });

              if (!wageRes.ok) {
                const errText = await wageRes.text();
                console.warn("Wage save failed:", errText);
                alert("Production saved but failed to save other costs.");
              }
            } catch (err) {
              console.error("Error saving wages:", err);
              alert("Production saved but failed to save other costs.");
            }
          }
        }

        navigate("/production_dashboard");
      } else {
        console.error("Save failed:", result);
        alert(`Failed to save ${config.title}.`);
      }
    } catch (err) {
      console.error("Error saving:", err);
      alert(`Error saving ${config.title}.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrint = () => {
    navigate("/production_dashboard");
  };

  const handleParentTabChange = (event, newValue) => {
    setParentTabValue(newValue);
  };

  return (
    <div className="w-full relative">
      <div className="flex items-center justify-between lg:px-10 px-2 mb-2 gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">Assembling / Dismantling</h1>
        </div>

        <Tooltip title={tooltipContent} sx={{ margin: 0, padding: 0 }}>
          <IconButton>
            <HelpOutline
              className="text-blue-600 bg-white rounded-full"
              fontSize="medium"
            />
          </IconButton>
        </Tooltip>
      </div>

      <ModeIndicator mode={mode} documentType={documentType} />

      <div className="w-full rounded-lg">
        <div className="lg:px-10">
          <div className="bg-gray-50 border lg:p-3 rounded-lg shadow pb-6">
            <ThemeProvider theme={theme}>
              <TabContext value={parentTabValue}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <TabList onChange={handleParentTabChange}>
                    <Tab label="General" value="1" sx={{ padding: 0 }} />
                    <Tab label="Timeline" value="3" sx={{ padding: 0 }} />
                  </TabList>
                  <div className="lg:flex hidden items-center gap-3">
                    <button
                      onClick={handlePrint}
                      className="flex items-center gap-2 px-5 py-2.5 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg shadow hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <View className="w-4 h-4" />
                      View
                    </button>
                    <button
                      onClick={() => {
                        if (mode === "edit") {
                          setIsModificationModalOpen(true);
                        } else {
                          handleSave();
                        }
                      }}
                      disabled={mode === "view" || isSaving}
                      className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          {mode === "edit" ? "Update" : "Save"}
                        </>
                      )}
                    </button>
                  </div>
                </Box>
                <TabPanel value="1" sx={{ p: 0, paddingX: 0 }}>
                  <AssembleDismantleGeneral
                    productionData={documentData}
                    setPurchaseInvoiceData={setDocumentData}
                    COMPANY_CODE={COMPANY_CODE}
                    DEPARTMENT={DEPARTMENT}
                    BASE_URL={BASE_URL}
                    setSupplierData={setSupplierData}
                    selectedSupplier={selectedSupplier}
                    setSelectedSupplier={setSelectedSupplier}
                    supplierData={supplierData}
                    documentType="PRODUCTION"
                    unitCostManual={unitCostManual}
                    setUnitCostManual={setUnitCostManual}
                    otherCosts={otherCosts}
                    setOtherCosts={setOtherCosts}
                    mode={mode}
                    setIsOtherCostModalOpen={setIsOtherCostModalOpen}
                    isOtherCostModalOpen={isOtherCostModalOpen}
                    handleProductSelect={handleProductSelect}
                    setIsStockManagementOpen={setIsStockManagementOpen}
                    isStockManagementOpen={isStockManagementOpen}
                    netamount={totals.netAmount}
                    currency={documentData.currency}
                    currencyRate={documentData.currencyRate}
                  />
                </TabPanel>
                <TabPanel value="3" sx={{ p: 1 }}>
                  <TimelineTab
                    BASE_URL={BASE_URL}
                    DEPARTMENT={DEPARTMENT}
                    modificationType={MODIFICATION_TYPES[documentType] || null}
                    invoiceId={documentNoFromState}
                    COMPANY_CODE={COMPANY_CODE}
                  />
                </TabPanel>
              </TabContext>
            </ThemeProvider>
          </div>
        </div>

        <main className="w-full mt-4 relative ">
          <ProductionTable
            items={items}
            onItemsChange={setItems}
            setShowSidebar={setShowSidebar}
            setItemInfo={setItemInfo}
            setCurrentLocation={setCurrentLocation}
            extraFields={[]}
            purchaseData={documentData}
            initialRowUnitOptions={initialRowUnitOptions}
            setInitialRowUnitOptions={setInitialRowUnitOptions}
            isOtherCostEnabled={isOtherCostEnabled}
            setIsOtherCostEnabled={setIsOtherCostEnabled}
            otherCosts={otherCosts}
            setOtherCosts={setOtherCosts}
            calculateRowValues={calculateRowValues}
            invoiceData={documentData}
            getVatPercent={getVatPercent}
            BASE_URL={BASE_URL}
            COMPANY_CODE={COMPANY_CODE}
            DEPARTMENT_NO={DEPARTMENT}
            hideAddButton={true}
          />
        </main>
      </div>

      <Modal
        isOpen={isModificationModalOpen}
        onClose={() => setIsModificationModalOpen(false)}
        size="md"
        header={true}
        title="Update Confirmation"
        showFullScreen={false}
      >
        <ModificationCommentModal
          isOpen={isModificationModalOpen}
          onClose={() => setIsModificationModalOpen(false)}
          handleSave={handleSave}
          documentData={documentData}
          documentType={documentType}
          COMPANY_CODE={COMPANY_CODE}
          DEPARTMENT={DEPARTMENT}
          CURRENT_USER={CURRENT_USER}
          BASE_URL={BASE_URL}
          setIsModificationModalOpen={setIsModificationModalOpen}
        />
      </Modal>
    </div>
  );
};

export default AssembleDisamble;
